<template>
	<div class="display-flex flex-column height-100 pb-8">
		<v-expansion-panels v-model="panel" flat accordion>
			<ExpansionPanel title="App">
				<ServiceSelector
					v-if="serviceRelatedActions.length === 0"
					:selectedApp="selectedIntegrationService"
					:menuProps="menuProps"
					:appItems="appItems"
					@setSearchString="(v) => (searchString = v)"
					@changeActionOrService="changeActionOrService"
					@setSelectedApp="(app) => (selectedIntegrationService = app)"
				/>
				<div class="ml-n1 mt-6 width-100 display-flex flex-row flex-align-center" v-else>
					<ConnectionServiceCard :serviceSlug="selectedIntegrationService?.service_slug" />
					<div class="service-name-text">
						{{ selectedIntegrationService?.service_name }}
					</div>
				</div>
			</ExpansionPanel>
			<ExpansionPanel v-if="selectedIntegrationService" title="Item type">
				<SelectionButton disabled v-if="selectedFieldItem">
					{{ selectedFieldItem }}
				</SelectionButton>
				<MapperItemEditor
					v-else
					:widgetFieldUUID="localFieldId"
					:service="selectedIntegrationService"
					@save-field-item="onFieldItemChanged"
				/>
			</ExpansionPanel>
			<ExpansionPanel v-if="selectedFieldItem" title="Connection">
				<ProfileConfigurationSection
					:serviceId="selectedIntegrationService?.id"
					:contextMode="false"
					:widgetFieldMode="true"
					:narrowLayout="true"
					:credentialsData="credentialsData"
					:credentialsType="credentialsType"
					:serviceVersionId="selectedServiceVersion"
					:overrideSelectProfile="overrideSelectProfile"
					:configurationFieldProps="configurationFieldProps"
				/>
			</ExpansionPanel>
			<ExpansionPanel v-if="selectedProfile" title="Mapping Editor">
				<MappingSetupInfo utilClasses="mt-4" />
				<div class="section-title">Connect your own data</div>
				<div class="section-description">
					The mapping block lets users define their own data structure in setup. Start by defining your own data.
				</div>
				<MapperBaseParamsEditorDispatcher
					class="my-3"
					:widgetFieldUUID="localFieldId"
					:service="selectedIntegrationService"
					:fieldItem="selectedFieldItem"
					@send-params-map="onParamsMapChanged"
					@send-params-map-labels="onParamsMapLabelsChanged"
				/>
				<MapperTitleEditor class="my-6" :existingTitle="mapperTitle" @set-title="onFieldTitleChanged" />
			</ExpansionPanel>
			<ExpansionPanel v-if="selectedProfile && isMappingSetupFinished" title="Mappings">
				<DynamicParamsMapperDispatcher
					:widgetFieldUUID="localFieldId"
					:service="selectedIntegrationService"
					:fieldItem="selectedFieldItem"
				/>
			</ExpansionPanel>
		</v-expansion-panels>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { getAllServicesList } from '@/store/modules/apps/getters';
import { userSpecificConfigurationId, generalConfigurationId } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { getConfiguration, getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { getAccountConnection } from '@/store/modules/credentials/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';

import ConnectionServiceCard from '@/views/Flow/configurations/SetupMenu/CredentialsSelector/ConnectionServiceCard';
import ExpansionPanel from '@/components/BlockEditorWidget/FieldSettings/WidgetSettings/ExpansionPanel';
import MappingSetupInfo from '@/components/BlockEditor/MappingSetup/MappingSetupInfo.vue';

import DynamicParamsMapperDispatcher from '@/components/BlockEditor/MappingSetup/DynamicParamsMapperDispatcher';
import MapperTitleEditor from '@/components/BlockEditor/MappingSetup/MapperTitleEditor';
import MapperItemEditor from '@/components/BlockEditor/MappingSetup/MapperItemEditor';

import MapperBaseParamsEditorDispatcher from '@/components/BlockEditor/MappingSetup/MapperBaseParamsEditorDispatcher';
import ServiceSelector from './common/ServiceSelector';
import LocalFieldMixin from './LocalFieldMixin';

import DynamicDataService from '@/utils/dynamic-data-service';
import { CredentialsTypes } from '@ply-io/ply-front-shared-utilities';

export default {
	name: 'UserSchemaMappingsSelectorFieldSettings',
	mixins: [LocalFieldMixin],
	components: {
		ExpansionPanel,
		ConnectionServiceCard,
		ServiceSelector,
		MappingSetupInfo,
		DynamicParamsMapperDispatcher,
		MapperTitleEditor,
		MapperItemEditor,
		MapperBaseParamsEditorDispatcher,
		ProfileConfigurationSection: () => import('@/components/common/ProfileSelector/ProfileConfigurationSection'),
	},
	computed: {
		...mapGetters([getAllServicesList.fullPath, 'widgetEditor/editField', 'apps/getService', 'widgetEditor/widgetFields']),
		...mapGetters([
			generalConfigurationId.fullPath,
			userSpecificConfigurationId.fullPath,
			getConfigurationFieldResponse.fullPath,
			getAccountConnection.fullPath,
		]),
		...mapGetters([getConfiguration.fullPath, getConfigurationAndInterfaceIdByFieldUUID.fullPath, editedWorkflowVersion.fullPath]),
		configurationAndInterfaceIdByFieldUUID() {
			return this[getConfigurationAndInterfaceIdByFieldUUID.fullPath](this.localFieldId);
		},
		configurationId() {
			return Number(this.configurationAndInterfaceIdByFieldUUID.configurationId);
		},
		serviceRelatedActions() {
			return this.localField.source_of_nodes || [];
		},
		integrationService() {
			return this.localField.source_of_nodes || [];
		},
		localFieldId() {
			return this['widgetEditor/editField']?.id;
		},
		menuProps() {
			return { maxHeight: 0 };
		},
		appItems() {
			return [...this[getAllServicesList.fullPath].filter((app) => app.param_mappings && Object.keys(app.param_mappings).length > 0)];
		},
		selectedIntegrationService: {
			get() {
				if (this.localField.metadata.source.service) {
					return this['apps/getService']([this.localField.metadata.source.service]);
				}
				return null;
			},
			set(newApp) {
				this.setLocalFieldKey('metadata.source.service', newApp?.id);
				this.setLocalFieldKey('metadata.source.service_version', newApp?.version);
				const title = newApp ? `${newApp.service_name}` : 'Map Data';
				this.updateFieldTitle(title);
				if (newApp) {
					this.openNextPanel();
				}
			},
		},
		selectedServiceVersion() {
			return this.localField.metadata.source.service_version;
		},
		selectedFieldItem() {
			return this.localField.metadata.source.field_item;
		},
		selectedProfileSourceField: {
			get() {
				return this.localField.metadata.source.credentials_configuration?.source_field_uuid;
			},
			set(value) {
				this.setLocalFieldKey('metadata.source.credentials_configuration.source_field_uuid', value);
			},
		},
		selectedProfileId() {
			if (!this.selectedProfileSourceField) {
				return null;
			}
			return this[getConfigurationFieldResponse.fullPath](this.configurationId, this.selectedProfileSourceField);
		},
		selectedProfile() {
			if (!this.selectedProfileId) {
				return null;
			}
			return this[getAccountConnection.fullPath](this.selectedProfileId);
		},
		stepsToApply: {
			get() {
				return this.localField.metadata.source.nodes || [];
			},
			set(value) {
				this.setLocalFieldKey('metadata.source.nodes', value);
			},
		},
		needToChooseFieldToMap() {
			return !this.credentialsFieldUuid;
		},
		credentialsFieldUuid: {
			get() {
				return this.localField.metadata.source.credentials_configuration?.source_field_uuid;
			},
			set(value) {
				console.log('credentialsFieldUuid: ', value);
				this.setLocalFieldKey('metadata.source.credentials_configuration.source_field_uuid', value);
			},
		},
		credentialsType: {
			get() {
				return this.localField.metadata.source.credentials_configuration.credentials_type;
			},
			set(value) {
				this.setLocalFieldKey('metadata.source.credentials_configuration.credentials_type', value);
			},
		},
		mapperTitle: {
			get() {
				return this.localField.metadata?.source?.field_title;
			},
			set(value) {
				this.onFieldTitleChanged(value);
			},
		},
		configurationType() {
			return this.configurationId === this[userSpecificConfigurationId.fullPath]
				? CredentialsTypes.ProfileBased
				: this.configurationId === this[generalConfigurationId.fullPath]
				? CredentialsTypes.AccountBased
				: '';
		},
		configurationFieldProps() {
			return {
				fieldUUID: this.credentialsFieldUuid,
				configurationId: this.configurationId,
			};
		},
		credentialsData() {
			return {
				credentials_type: this.credentialsType,
				credentials_field_uuid: this.credentialsFieldUuid,
			};
		},
		isMappingSetupFinished() {
			return DynamicDataService.isMappingSetupFinished(this.localField, this.selectedIntegrationService);
		},
	},
	methods: {
		...mapActions('widgetEditor', ['syncWidgetEditorWithWidgetsStore']),
		changeActionOrService() {
			this.selectedIntegrationService = null;
			this.stepsToApply = [];
		},
		onFieldItemChanged(value) {
			this.setLocalFieldKey('metadata.source.field_item', value);
		},
		onFieldTitleChanged(value) {
			this.setLocalFieldKey('metadata.source.field_title', value);
		},
		onParamsMapChanged() {
			this.syncWidgetEditorWithWidgetsStore();
		},
		onParamsMapLabelsChanged() {
			this.syncWidgetEditorWithWidgetsStore();
		},
		updateFieldTitle(value) {
			if (!('identifier' in this.localField.metadata.options)) {
				this.fieldIdentifier = this.titleValue;
			}

			const needToUpdateIdentifier = this.fieldIdentifier === this.titleValue;

			this.titleValue = value;
			if (needToUpdateIdentifier) {
				this.fieldIdentifier = value;
			}
		},
		openNextPanel() {
			this.panel = this.panel + 1;
		},
		onDynamicFieldsUpdate() {
			console.log('**** onDynamicFieldsUpdate');
		},
		getNewWidgetField(field) {
			const fieldIndex = this['widgetEditor/widgetFields'].length;
			return {
				payload: field,
				addedIndex: fieldIndex,
				removedIndex: null,
			};
		},
		overrideSelectProfile(_, sourceFieldUUID) {
			this.selectedProfileSourceField = sourceFieldUUID;
		},
	},
	created() {
		this.panel = Number(!!this.selectedIntegrationService);
		if (!this.credentialsType) {
			this.credentialsType = this.configurationType;
		}
	},
	data() {
		return {
			panel: 0,
			searchString: '',
			selectedCredentialsId: null,
		};
	},
};
</script>

<style scoped lang="scss">
.service-name-text {
	font-size: 14px;
	font-weight: 500;
	color: var(--primary-font-color);
}

.section-title {
	font-size: 16px;
	font-weight: 600;
}
.section-description {
	margin: 2px 0 4px;
	font-size: 11px;
}
</style>
